import * as amplitude from "@amplitude/analytics-browser";
import { Button, Flex, Spacer, Text, Tooltip } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik, FormikHelpers, FormikValues } from "formik";
import React, { useMemo } from "react";
import { RiEditLine } from "react-icons/ri";
import { useSearchParams } from "react-router-dom";
import { Item } from "src/components/Inputs/SearchSelect/SearchSelect";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import {
  NavigationBlockerProvider,
  WithNavigationBlocker,
} from "src/components/NavigationBlocker";
import { SearchAndFilterTypes } from "src/constants";
import * as FSQ from "src/generated/hooks/form-service/queries";
import {
  CapacityConfig,
  updateCapacitiesByEnrollmentPeriod,
  UpdateCapacitiesByEnrollmentPeriodData,
} from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { validateWithZod } from "src/services/formValidations";
import { isNonEmptyString } from "src/services/predicates";
import * as RD from "src/types/remoteData";
import { CapacityTable } from "./CapacityTable";
import {
  Capacity,
  FormikSchema,
  FormikType,
  toFormikInitialValues,
} from "./Formik";
import { SchoolSearchSelect } from "./SchoolSearchSelect";

type Props = {
  enrollmentPeriodId: string;
  schoolId: string;
  data: { capacities: Capacity[]; config: CapacityConfig };
  closeEdit: () => void;
};

export const CapacitiesEdit: React.FC<Props> = ({
  enrollmentPeriodId,
  schoolId,
  data,
  closeEdit,
}) => {
  const avelaToast = useAvelaToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialValues = useMemo(() => {
    return toFormikInitialValues(data.capacities);
  }, [data]);

  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: "Update capacities?",
    body: "",
    confirmButton: {
      label: "Yes, update",
    },
    cancelButton: {
      label: "No, cancel",
    },
  });

  const onSubmit = React.useCallback(
    async (
      values: FormikValues,
      formikHelpers: FormikHelpers<FormikValues>
    ): Promise<void> => {
      amplitude.track("Clicked update");
      const additionalSeatsEnabled =
        data.config.additionalSeatsTaken1.enabled ||
        data.config.additionalSeatsTaken2.enabled;
      if (
        !(await confirm({
          onConfirm: () => {
            amplitude.track("Clicked confirm update");
          },
          body: additionalSeatsEnabled
            ? "Changes to additional seats and total capacities may impact making offers and filters by capacities. This can't be undone or recovered."
            : "Changes to total capacities may impact making offers and filters by capacities. This can't be undone or recovered.",
        }))
      ) {
        return;
      }

      const toastId = avelaToast({
        title: "Updating capacities",
        status: "loading",
        duration: null,
      });
      try {
        await updateCapacitiesByEnrollmentPeriod({
          path: {
            enrollmentPeriodId,
          },
          body: toUpdateCapacities(values),
        });
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [FSQ.useGetCapacitiesBySchoolAndEnrollmentPeriodKey],
            exact: false,
          }),
          queryClient.invalidateQueries({
            queryKey: [FSQ.useGetCapacitySummaryByEnrollmentPeriodKey],
            exact: false,
          }),
          queryClient.invalidateQueries({
            queryKey: [FSQ.useGetCapacitiesHistoryKey],
            exact: false,
          }),
        ]);
        formikHelpers.resetForm({ values });
        closeEdit();
        avelaToast({
          status: "success",
          title: "Capacities updated",
        });
      } catch (error) {
        console.error(error);
        avelaToast.genericError();
      } finally {
        avelaToast.close(toastId);
      }
    },
    [avelaToast, data, closeEdit, confirm, enrollmentPeriodId, queryClient]
  );

  const capacityRD = React.useMemo(() => {
    return RD.success(data);
  }, [data]);

  if (!isNonEmptyString(schoolId)) {
    return null;
  }

  return (
    <NavigationBlockerProvider
      includeQueryParams={true}
      confirmationConfig={{
        dialogProps: {
          body: (
            <Text>
              There are{" "}
              <Text as="span" fontWeight={600}>
                unsaved changes on this capacities
              </Text>
              . If you exit now, you will lose your changes.
            </Text>
          ),
        },
      }}
    >
      <Formik<FormikType>
        initialValues={initialValues}
        validate={validateWithZod(FormikSchema)}
        onSubmit={onSubmit}
      >
        {(formikContext) => (
          <WithNavigationBlocker hasChanges={formikContext.dirty}>
            {(confirm) => (
              <Flex
                as={Form}
                direction="column"
                height="100%"
                flexGrow="1"
                gap="4"
              >
                <SchoolSearchSelect
                  enrollmentPeriodId={enrollmentPeriodId}
                  schoolId={schoolId}
                  onChange={async (item: Item | null) => {
                    if (!(await confirm())) {
                      return;
                    }

                    if (item === null) {
                      searchParams.delete(SearchAndFilterTypes.School);
                    } else {
                      searchParams.set(SearchAndFilterTypes.School, item.key);
                    }
                    setSearchParams(searchParams);
                    closeEdit();
                  }}
                />

                <CapacityTable
                  remoteData={capacityRD}
                  schoolId={schoolId}
                  enrollmentPeriodId={enrollmentPeriodId}
                  mode="edit"
                  additionalNodes={
                    <Tooltip label="Currently editing capacities" hasArrow>
                      <Button
                        marginLeft="2"
                        size="sm"
                        isDisabled
                        leftIcon={<RiEditLine />}
                      >
                        Edit
                      </Button>
                    </Tooltip>
                  }
                />
                <Spacer />
                {confirmationDialog}
                <AdminFormButtons insideTabPanel>
                  <Button
                    variant="outline"
                    colorScheme="gray"
                    type="button"
                    onClick={async () => {
                      amplitude.track("Clicked cancel update");
                      if (await confirm()) {
                        closeEdit();
                      }
                    }}
                  >
                    Cancel
                  </Button>
                  <Spacer />
                  <Button type="submit">Update</Button>
                </AdminFormButtons>
              </Flex>
            )}
          </WithNavigationBlocker>
        )}
      </Formik>
    </NavigationBlockerProvider>
  );
};

type UpdateCapacity = UpdateCapacitiesByEnrollmentPeriodData["body"][0];
function toUpdateCapacities(values: FormikType): UpdateCapacity[] {
  const body: UpdateCapacity[] = [];
  for (const id in values) {
    const row = values[id];
    if (row === undefined) continue;
    const totalCapacity = parseInt(row.totalCapacity);
    const additionalSeatsTaken1 = parseInt(row.additionalSeatsTaken1);
    const additionalSeatsTaken2 = parseInt(row.additionalSeatsTaken2);
    body.push({
      id,
      totalCapacity,
      additionalSeatsTaken1,
      additionalSeatsTaken2,
    });
  }
  return body;
}
